import { Button, Group, Modal, SegmentedControl, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconArrowLeft, IconDeviceFloppy, IconEye } from '@tabler/icons-react';

export type TemplateEditorMode = 'blocks' | 'advanced';

interface ModeBarProps {
  mode: TemplateEditorMode;
  // Called after the user confirms (Blocks→Advanced) or unconditionally
  // (Advanced→Blocks). Switching direction is the caller's concern — they
  // run the parser, derive new state, etc.
  onModeChange: (next: TemplateEditorMode) => void;

  onBack: () => void;
  onPreview: () => void;
  onSave: () => void;
  saving: boolean;
  // Disable Blocks mode if there's something unconvertable in the
  // current Jinja string (set by the parent based on parse result).
  blocksUnavailable?: boolean;
  blocksUnavailableReason?: string;
}

export function ModeBar({
  mode,
  onModeChange,
  onBack,
  onPreview,
  onSave,
  saving,
  blocksUnavailable,
  blocksUnavailableReason,
}: Readonly<ModeBarProps>) {
  const [confirmOpen, confirmCtl] = useDisclosure(false);

  const handleSegmentChange = (value: string) => {
    const next = value as TemplateEditorMode;
    if (next === mode) {
      return;
    }
    if (mode === 'blocks' && next === 'advanced') {
      // Destructive: confirm.
      confirmCtl.open();
      return;
    }
    onModeChange(next);
  };

  return (
    <>
      <Group
        justify="space-between"
        px="md"
        py="xs"
        style={{
          borderBottom: '1px solid var(--mantine-color-default-border)',
          background: 'var(--mantine-color-default-hover)',
        }}
      >
        <Group gap="sm">
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconArrowLeft size={14} />}
            onClick={onBack}
          >
            Back to config
          </Button>
          <SegmentedControl
            size="xs"
            value={mode}
            onChange={handleSegmentChange}
            data={[
              {
                label: 'Blocks',
                value: 'blocks',
                disabled: blocksUnavailable && mode !== 'blocks',
              },
              { label: 'Advanced', value: 'advanced' },
            ]}
          />
          {blocksUnavailable && mode === 'advanced' && blocksUnavailableReason && (
            <Text size="xs" c="orange">
              {blocksUnavailableReason}
            </Text>
          )}
        </Group>
        <Group gap="xs">
          <Button
            size="xs"
            variant="default"
            leftSection={<IconEye size={14} />}
            onClick={onPreview}
          >
            Preview
          </Button>
          <Button
            size="xs"
            leftSection={<IconDeviceFloppy size={14} />}
            onClick={onSave}
            loading={saving}
          >
            Save
          </Button>
        </Group>
      </Group>

      <Modal
        opened={confirmOpen}
        onClose={confirmCtl.close}
        title="Switch to advanced editor?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Switching to the advanced editor will discard the block view of this template. You can
            still edit the raw Jinja, but converting back to blocks may not recover the same
            structure.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={confirmCtl.close}>
              Cancel
            </Button>
            <Button
              color="orange"
              onClick={() => {
                confirmCtl.close();
                onModeChange('advanced');
              }}
            >
              Switch to advanced
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
